import React from 'react'
import { css } from '@linaria/core'
import { LayoutBand, Row, WhiteTheme, useContainerWidth, Cell, Expander, ExpanderContent } from '@fs/zion-ui'
import { i18n } from '@fs/zion-locale'
import RootsTechBannerViewProvider, { viewTypes } from './internal/RootsTechBannerViewProvider'
import { useRootsTechBannerContext } from './internal/RootsTechBannerProvider'
import RootsTechBannerBackground from './internal/RootsTechBannerBackground'
import RootsTechLogo from './internal/RootsTechLogo'
import RootsTechBannerOpenCloseIcon from './internal/RootsTechBannerOpenCloseIcon'
import RootsTechBannerText from './RootsTechBannerText'
import RootsTechBannerButton from './RootsTechBannerButton'

const verticalBar = css`
  border-inline-start: solid white 1px;
  height: 43px;
  display: inline;
`

export default function RootsTechBannerCollapsed({
  children,
  backgroundImage,
  backgroundSize,
  backgroundPosition,
  backgroundColor,
}) {
  const atSize = useContainerWidth()
  const isTablet = atSize({ default: false, lg: true, xl: false }) // we hide "expand" text if tablet size
  let isMobile = atSize({ default: true, lg: false })

  let bannerText = null
  let bannerButton = null
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      if (child.type === RootsTechBannerText) {
        bannerText = child
      } else if (child.type === RootsTechBannerButton) {
        bannerButton = child
      }
    }
  })

  // Special handling for Russian language where the button text is long
  if (bannerButton?.props?.children?.length > 25 && i18n.language === 'ru' && atSize({ default: true, xxl: false })) {
    isMobile = true
  }

  return (
    <RootsTechBannerCollapsedWrapper>
      <RootsTechBannerViewProvider viewType={viewTypes.COLLAPSED}>
        <RootsTechBannerBackground
          backgroundImage={backgroundImage}
          backgroundSize={backgroundSize}
          backgroundPosition={backgroundPosition}
          backgroundColor={backgroundColor}
        >
          <WhiteTheme>
            {isMobile ? (
              <LayoutBand alignY="middle" marginY="sm" maxWidth="xxl" marginX="sm">
                <MobileLayout bannerText={bannerText} bannerButton={bannerButton} />
              </LayoutBand>
            ) : (
              <DesktopLayout bannerText={bannerText} bannerButton={bannerButton} isTabletSize={isTablet} />
            )}
          </WhiteTheme>
        </RootsTechBannerBackground>
      </RootsTechBannerViewProvider>
    </RootsTechBannerCollapsedWrapper>
  )
}

function DesktopLayout({ bannerText, bannerButton, isTabletSize }) {
  const { canToggle } = useRootsTechBannerContext()
  return (
    <Row alignY="middle" data-testid="rootstech-banner-desktop-layout">
      <Cell growX>
        <LayoutBand alignY="middle" marginY="sm">
          <Row alignY="middle" guttersX="sm">
            <Cell noShrinkX alignY="middle">
              <RootsTechLogo height="40px" />
            </Cell>
            <Cell noShrinkX alignY="middle">
              <div className={verticalBar} aria-hidden="true" />
            </Cell>
            <Cell growX alignY="middle">
              {bannerText}
            </Cell>
          </Row>
        </LayoutBand>
      </Cell>
      <Cell noShrinkX alignY="middle">
        <Row alignY="middle" guttersX="sm">
          <Row alignX="end" alignY="middle" guttersX="sm">
            <Cell noShrinkX alignY="middle">
              {bannerButton}
            </Cell>
            {canToggle && (
              <Cell noShrinkX alignY="middle">
                <LayoutBand marginX="xs">
                  <RootsTechBannerOpenCloseIcon isTablet={isTabletSize} />
                </LayoutBand>
              </Cell>
            )}
          </Row>
        </Row>
      </Cell>
    </Row>
  )
}

function MobileLayout({ bannerText, bannerButton }) {
  const { canToggle } = useRootsTechBannerContext()
  return (
    <div data-testid="rootstech-banner-mobile-layout">
      <Row alignY="middle" gutters="sm">
        <Cell growX>
          <RootsTechLogo height="36px" />
        </Cell>
      </Row>
      <Row alignY="middle" gutters="sm">
        <Cell growX>{bannerText}</Cell>
      </Row>
      <Row alignY="middle" gutters="sm">
        <Cell>{bannerButton}</Cell>
        {canToggle && (
          <Cell noShrinkX alignX="end">
            <RootsTechBannerOpenCloseIcon />
          </Cell>
        )}
      </Row>
    </div>
  )
}

function RootsTechBannerCollapsedWrapper({ children }) {
  const { isOpen, canToggle } = useRootsTechBannerContext()
  if (!canToggle) {
    return <>{children}</>
  }
  return (
    <Expander open={!isOpen} setOpen={() => {}}>
      <ExpanderContent>{children}</ExpanderContent>
    </Expander>
  )
}
